<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-auto-qa-panel-dynamic-chart">
    <div ref="panelRef" class="dynamic-wrapper">
      <div v-loading="controller.dynamicChartsLoading" ref="lineChartPanel" class="panel-wrapper">
        <div class="panel-header">{{ lineChartTitleMapper.autoEvalScore }}</div>
        <div v-if="showLineChart" class="panel-body">
          <div class="info-section">
            <div class="date-range-info">
              <span>{{dataInfo}}</span>
            </div>
            <div class="panel-dropdown">
              <el-select ref="viewBySelect" v-model="selectedDateRange" collapse-tags size="mini" class="date-range-dropdown" popper-class="wy-auto-qa-date-range-popper">
                <el-option
                  v-for="item in dateRangeTypes"
                  @click.native="handleLineChartViewBy(item.type, item.disabled)"
                  :disabled="item.disabled"
                  :key="item.type"
                  :label="item.label"
                  :value="item.type"
                >
                </el-option>
              </el-select>
            </div>
          </div>
          
          <div ref="lineChart" class="dynamic-line-chart"></div>
        </div>
        <div v-else class="panel-body-no-data">
          <span class="content">No data available yet for this brand or time period. Please use the global filter to select a brand and time period with data available.</span>
        </div>
        <div v-if="showLineChart&&!legends" class="panel-footer">
          <span class="footer-col">
            <svg xmlns="http://www.w3.org/2000/svg" width="50" height="3" viewBox="0 0 50 3" fill="none">
              <path d="M0 1.76221H20" :stroke="legendColors[0]" stroke-width="2" stroke-linecap="round" transform="scale(2.5, 1)" />
            </svg>
            {{ lineChartGraphMapper[lineChartTitleMapper.autoEvalScore] }}
          </span>
        </div>
        <div v-if="showLineChart&&legends" class="panel-footer">
          <span class="footer-col"  v-for="(legend, index) in legends" :key="index">
            <svg xmlns="http://www.w3.org/2000/svg" width="50" height="3" viewBox="0 0 50 3" fill="none">
              <path d="M0 1.76221H20" :stroke="legendColors[index]" stroke-width="2" stroke-linecap="round" :stroke-dasharray="index>0?'5 5':''" transform="scale(2.5, 1)" />
            </svg>
            {{ legend }}
          </span>
        </div>
      </div>
      <div v-if="showBarChart" v-loading="controller.dynamicChartsLoading" ref="waterfallChartPanel" class="panel-wrapper">
        <div class="panel-header">{{ waterfallChartFooterMapper.autoEvalScore }}</div>
        <div v-if="barChartHasData" class="panel-body-waterfall">
          <div ref="waterfallChart"></div>
          <!-- <div style="display: flex; align-items: center; justify-content: center; gap: 10px; marginTop: 10px">
            <div style="display: flex; align-items: center; justify-content: center;">
              <div style="height: 10px; width: 1px; background-color: var(--site-font-tertiary-color); "></div>
              <div :style="waterfallHrStyle"></div>
            </div>
            <div style="fontWeight: 400: fontSize: 1em">{{ waterfallChartFooterMapper[selectedGaugePanel] }}</div>
            <div style="display: flex; align-items: center; justify-content: center;">
              <div :style="waterfallHrStyle"></div>
              <div style="height: 10px; width: 1px; background-color: var(--site-font-tertiary-color); "></div>
            </div>
          </div> -->
          <div ref="hoverChart" id='hoverinfo' class="hover-chart"></div>
        </div>
        <div v-else class="panel-body-no-data">
          <span class="content">No data available yet for this brand or time period. Please use the global filter to select a brand and time period with data available.</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import { formatLabel } from './waterfall-chart-util';
import { mapGetters } from 'vuex';
import { formatDateRangeForPlotly, initDateRangeRules, groupByDateRangeForAvgVolume, groupByDateRangeForAvgVolumeWithOverAll } from './view-by-date-range-util';
import WyChartModule from '../../../../library/wy-chart-module';

export default Vue.extend({
  name: 'wy-auto-qa-panel-dynamic-chart',
  props: ['controller', 'lineChartTitleMapper', 'waterfallChartFooterMapper', 'legends'],
  data() {
    return {
      wyChart: null,

      waterfallHrResizeTimeout: null,
      waterfallHrStyle: '0px',

      selectedDateRange: null,
      dataInfo: '',

      // both modified in handlePanelChange method in gauge-summary component
      showLineChart: true,
      showBarChart: true,
      barChartHasData: false,

      showFooterTarget: false,

      networkData: {
        autoEvalScore: null
      },

      lineChartGraphMapper: {
        'Auto evaluation score trend': 'AES of all agents',
      },

      legendColors: ['#080687', '#FFB488'],

      // lineChartTitleMapper: {
      //   autoEvalScore: 'Auto evaluation score trend'
      // },
      // waterfallChartFooterMapper: {
      //   autoEvalScore: 'Auto evaluation sections'
      // },

      waterfallChartBarColors: ['#598EAC', '#7AABB9', '#0AA1CC', '#6096DF', '#2061D3','#0B38AB' ],

      dateRangeTypes: {
        year: {
          type: 'year',
          label: 'View by year',
          tickformat: '%Y',
          disabled: true,
          duraion: 99999,
          selected: this.selectedDateRange === 'year',
        },
        month: {
          type: 'month',
          label: 'View by month',
          tickformat: '%b %Y',
          disabled: true,
          duraion: 365,
          selected: this.selectedDateRange === 'month',
        },
        week: {
          type: 'week',
          label: 'View by week',
          tickformat: '%b %d %Y',
          disabled: true,
          duraion: 30,
          selected: this.selectedDateRange === 'week',
        },
        day: {
          type: 'day',
          label: 'View by day',
          tickformat: '%b %d %Y',
          disabled: true,
          duraion: 7,
          selected: this.selectedDateRange === 'day',
        },
      },
    };
  },

  computed: {
    ...mapGetters(['dataModel', 'companyConfig', 'config']),
  },

  methods: {
    handleLineChartViewBy(range, isDisabled) {
      if (isDisabled) return;

      this.selectedDateRange = this.dateRangeTypes[range].type;
      setTimeout(() => {
        this.plotLineChart();
      }, 10);
    },

    resetNetworkData() {
      this.networkData = {
        autoEvalScore: null
      };

      this.selectedDateRange = null;
    },

    reloadBothPanels() {
      setTimeout(() => {
        // Internal timeout, plotly takes some time to render the chart
        this.plotLineChart();
        this.plotBarChart();
        this.controller.dynamicChartsLoading = false;
      }, 10);
    },

    handleWindowResize() {
      // Debounce the resize event to avoid multiple recalculations during rapid resizing
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      this.resizeTimeout = setTimeout(() => {
        this.calculateWaterfallHrLine();
      }, 200); // Adjust the debounce time (in milliseconds) as per your needs
    },

    calculateWaterfallHrLine() {
      const chartContainerWidth = this.$refs.waterfallChart && this.$refs.waterfallChart.offsetWidth;
      const secondBarX = (chartContainerWidth / 8) * 2; // 8 bars in waterfall chart
      const seventhBarX = (chartContainerWidth / 8) * 7;
      const lineWidth = seventhBarX - secondBarX;

      this.waterfallHrStyle = {
        width: `${lineWidth / 2 - 100}px`,
        height: '1px',
        backgroundColor: 'var(--site-font-tertiary-color)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      };
    },

    plotLineChart() {
      
      if (!this.selectedDateRange) {
        // make sure when global filter is changed, we set this.selectedDateRange to null
        const dateStartMoment = moment(this.dataModel.dateStart, 'YYYY-MM-DD');
        const dateEndMoment = moment(this.dataModel.dateEnd, 'YYYY-MM-DD');
        this.dataInfo = dateStartMoment.format('MMM DD YYYY') + ' - ' + dateEndMoment.format('MMM DD YYYY')
        const dateRangeDatesDiff = dateEndMoment.diff(dateStartMoment, 'days', true) + 1;

        this.selectedDateRange = initDateRangeRules({
          dateRangeDatesDiff,
          dateRangeTypes: this.dateRangeTypes,
          companyConfigViewBy: (this.companyConfig.default || {}).selectedViewBy || 'week',
        });
      }

      const panelWrapperRefs = this.$refs.panelRef;
      if (!panelWrapperRefs) return;

      panelWrapperRefs.style.gridTemplateAreas = 'item item';
      panelWrapperRefs.style.gridTemplateColumns = '1fr 1fr';
      panelWrapperRefs.style.gridGap = '18px';

      const selectedData = this.networkData.autoEvalScore;
      const fullWidthMode = !this.showBarChart; // handled in gauge-summary.handlePanelChange logic

      const lineChartRefs = this.$refs.lineChart;
      const panelDivs = this.$refs.panelRef && this.$refs.panelRef.children && this.$refs.panelRef.children[0];

      if (fullWidthMode) {
        panelWrapperRefs.style.gridTemplateAreas = 'item';
        panelWrapperRefs.style.gridTemplateColumns = '1fr';
        panelWrapperRefs.style.gridGap = '0px';
      }
      if(lineChartRefs) {
        lineChartRefs.style.display = 'block';
        lineChartRefs.style.justifyContent = 'unset';
        lineChartRefs.style.minWidth = 'unset';
      }
      for (let i = 0; i < panelDivs.children.length; i++) {
        panelDivs.children[i].style.minWidth = 'unset';
      }

      const metrics = (selectedData && selectedData.dailyMetrics) || [];
      metrics.forEach((metric) => {
        if (metric.value == '-1') metric.value = null;
        if (metric && metric.rawData && metric.rawData.TotalBESScore == '-1') metric.rawData.TotalBESScore = null;
        if (metric && metric.rawData && metric.rawData.TotalBASScore == '-1') metric.rawData.TotalBASScore = null;
        if (metric && metric.rawData && metric.rawData.besScoreVolume == '-1') metric.rawData.besScoreVolume = null;
        if (metric && metric.rawData && metric.rawData.basScoreVolume == '-1') metric.rawData.basScoreVolume = null;

        this.wyChart.prepareGroupDates(metric, metric.date);
      });


    let groupedMetrics = null;
    groupedMetrics = groupByDateRangeForAvgVolume({ selectedDateRange: this.selectedDateRange, metrics: metrics });
    const xVal = formatDateRangeForPlotly({ selectedDateRange: this.selectedDateRange, groupedMetrics: groupedMetrics, dateStart: metrics.length && metrics[0] && metrics[0].day });
    const showMarkerMinCount = -1;
    let yVal = groupedMetrics.map((metric) => {
      return metric.value === null ? null : metric.value.toFixed(2);
    });
    
    yVal = yVal.map((val) => {
      if (val != 0 && val == null) {
        val = '-1';
      }
      else if(val < 0.01) {
        val = 0;
      }
      else if(val == Number.POSITIVE_INFINITY) {
        val = '-1';
      }
      return val;
    });

    const hoverTemplate = yVal.map((val) => {
      if (val == '-1') {
        return 'N/A';
      }
      // if (this.selectedGaugePanel === AvailableGaugePanels.volume) {
        // return `<b>${this.lineChartGraphMapper[this.lineChartTitleMapper.autoEvalScore] + '<br />%{y:,}'}</b><extra></extra>`;
      // }
      return val.toString();
    });

    let targetVal = null;
    this.showFooterTarget = true;

    this.showFooterTarget = targetVal != null;
    const lineTrace = {
      x: xVal, // xVal
      y: yVal,
      name: '',
      type: 'scatter',
      mode: 'lines+markers',
      cliponaxis: false,
      hoverinfo: 'x',

      marker: { color: (showMarkerMinCount == -1 || xVal.length <= showMarkerMinCount) ? this.legendColors[0] : 'transparent' },
      line: {
        width: 1,
        color: this.legendColors[0],
        shape: 'spline',
      },
      hovertemplate: hoverTemplate,
      hoverlabel: {
        font: {
          size: 14,
          family: 'Helvetica Neue, Helvetica, arial, sans-serif',
        },
      },
      fill: 'none',
    };
    let horizontalLineTrace = {}
    if(this.legends && this.legends.length > 1) {
      let metricsOverall = (selectedData && selectedData.dailyMetrics) || [];
      metricsOverall.forEach((metric) => {
        if (metric.valueOverall == '-1') metric.valueOverall = null;
        if (metric && metric.rawData && metric.rawData.totalAutoEvalScoreOverall == '-1') metric.rawData.totalAutoEvalScoreOverall = null;
        if (metric && metric.rawData && metric.rawData.TotalBASScore == '-1') metric.rawData.TotalBASScore = null;
        if (metric && metric.rawData && metric.rawData.besScoreVolume == '-1') metric.rawData.besScoreVolume = null;
        if (metric && metric.rawData && metric.rawData.basScoreVolume == '-1') metric.rawData.basScoreVolume = null;

        this.wyChart.prepareGroupDates(metric, metric.date);
      });
      let groupedMetricsOverAll = null;
      groupedMetricsOverAll = groupByDateRangeForAvgVolumeWithOverAll({ selectedDateRange: this.selectedDateRange, metrics: metrics });
      const xValOverAll = formatDateRangeForPlotly({ selectedDateRange: this.selectedDateRange, groupedMetrics: groupedMetricsOverAll, dateStart: metrics.length && metrics[0] && metrics[0].day });
      const showMarkerMinCountOverall = -1;
      let yValOverall = groupedMetricsOverAll.map((metric) => {
        return metric.value === null ? null : metric.value.toFixed(2);
      });
      // yValOverall = yValOverall.map((val) => {
      //   if (val != 0 && val == null) {
      //     val = '-1';
      //   }
      //   else if(val < 0.01) {
      //     val = 0;
      //   }
      //   else if(val == Number.POSITIVE_INFINITY) {
      //     val = '-1';
      //   }
      //   return val;
      // });
      // const hoverTemplateOverall = yValOverall.map((val) => {
      //   if (val == '-1') {
      //     return 'N/A';
      //   }
      //   return val.toString();
      // });
      // console.log("yValOverall", yValOverall, xValOverAll)
      horizontalLineTrace = {
        x: xValOverAll, // xVal
        y: yValOverall,
        name: '',
        type: 'scatter',
        mode: 'lines+markers',
        hoverinfo: 'x',
        cliponaxis: false,
        marker: { color: (showMarkerMinCountOverall == -1 || xValOverAll.length <= showMarkerMinCountOverall) ? this.legendColors[1] : 'transparent' },
        line: {
          color: this.legendColors[1],
          width: 1,
          dash: 'dash',
          shape: 'spline',
        },
        // hovertemplate: hoverTemplateOverall,
        hovertemplate: `%{y:.2f}</b><extra></extra>`,
        hoverlabel: {
          font: {
            size: 14,
            family: 'Helvetica Neue, Helvetica, arial, sans-serif',
          },
        },
        fill: 'none',
      };
    }

      let traceData = [];
      traceData = (this.legends && this.legends.length > 1) ? [lineTrace, horizontalLineTrace] : [lineTrace];

      const tickFormat = this.dateRangeTypes[this.selectedDateRange].tickformat || '%b %d %Y';

      const layout = {
        ...({}),
        autosize: true,
        paper_bgcolor: '#f5f5f5',
        plot_bgcolor: '#f5f5f5',
        showlegend: false,
        hovermode: 'x',
        margin: {
          t: 8,
          pad: 8,
        },
        xaxis: {
          type: 'date',
          showgrid: false,
          zeroline: false,
          showline: true,
          tickformat: tickFormat,
          fixedrange: true,
          autorange: true,
          tickfont: {
            size: 12,
            family: 'Helvetica Neue, Helvetica, Arial, sans-serif',
          },
          ticks: 'outside',
          tickcolor: 'white',
        },
        yaxis: {
          type: 'linear',
          side: 'left',
          title: 'Average Score',
          showgrid: true,
          zeroline: false,
          rangemode: 'nonnegative',
          tickmode: 'array',
          autorange: true,
          automargin: true,
          fixedrange: true,
          // tickprefix: this.selectedGaugePanel === AvailableGaugePanels.automated_chat ? '$' : null,
          tickfont: {
            size: 12,
            family: 'Helvetica Neue, Helvetica, Arial, sans-serif',
          },
        },
      };

      if (xVal.length > 1 && xVal.length < 5) {
        layout.xaxis.nticks = xVal.length;
        if (this.selectedDateRange === 'year') {
          layout.xaxis.tickformat = null;
        }
      }

      const viewingByYear = xVal.length == 1;
      const viewingByWeek = xVal.length == 5;
      const viewingByDays = xVal.length > 5;

      if (viewingByYear) {
        layout.xaxis.nticks = 3;
      }
      if (xVal.length < 5) {
        layout.xaxis.nticks = xVal.length;
      }

      if (viewingByDays) {
        layout.xaxis.tickmode = 'array';
        layout.xaxis.tickvals = [xVal[0], xVal[Math.round((xVal.length - 1) / 3)], xVal[Math.round((xVal.length - 1) / 3) * 2], xVal[xVal.length - 1]];
        layout.xaxis.nticks = 3;
      }

      if (viewingByWeek) {
        layout.xaxis.tickmode = 'array';
        layout.xaxis.tickvals = [xVal[0], xVal[Math.round((xVal.length - 1) / 2)], xVal[xVal.length - 1]];
        layout.xaxis.nticks = 3;
      }

      // if ((this.selectedGaugePanel === AvailableGaugePanels.automated_chat || this.selectedGaugePanel === AvailableGaugePanels.volume) && fullWidthMode) {
      //   layout.margin = {
      //     ...layout.margin,
      //     t: 40,
      //     b: 80,
      //     l: 100,
      //     r: 100,
      //   };
      // }

      // setting vertical boundary for y axis
      // if (this.selectedGaugePanel === AvailableGaugePanels.bes) {
        const filteredYVal = yVal.filter(val => val != '-1')
        let minValue = targetVal != null && targetVal < Math.min(...filteredYVal) ? targetVal : Math.min(...filteredYVal);
        let maxValue = targetVal > Math.max(...filteredYVal) ? targetVal : Math.max(...filteredYVal);
        let diffValue = parseInt(maxValue - minValue);
        layout.yaxis.nticks = diffValue > 5 ? 5 : diffValue;
        // let step = maxValue - minValue > 10 ? 10 : 1;
        const yAxisLayout = {
          // showgrid: true,
          // range: [parseInt(minValue / 10) * 10 > 100 ? 100 : parseInt(minValue / 10) * 10, maxValue + step * 2],
          range: [0, 100],
          autorange: false,
          tickmode: null,
        };
        layout.yaxis = {
          ...layout.yaxis,
          ...yAxisLayout,
        };
      traceData[0].y.forEach((yData, index) => {
        if(yData == '-1') {
          traceData[0].y[index] = null;
        }
      });
      // console.log("ssssss",traceData)
      // if(traceData[1] && traceData[1].y.length > 0) {
      //   traceData[1].y.forEach((yData, index) => {
      //     if(yData == '-1') {
      //       traceData[1].y[index] = null;
      //     }
      //   });
      // }
      if (lineChartRefs) {
        // eslint-disable-next-line no-undef
        let pl = Plotly;
        pl.newPlot(lineChartRefs, traceData, layout, { responsive: true, displayModeBar: false });
        
        // let targetNumber = 0;
        // this.$refs.lineChart.on('plotly_hover', (data) => {
        //   try {
        //     data.points.forEach((point) => {
        //       let dataPoint = point; //data.points[0];
        //       if (dataPoint && dataPoint.data && dataPoint.data.marker) {
        //         let pn = dataPoint.pointNumber;
        //         let colors = [];
        //         if(showMarkerMinCount != -1 && xVal.length > showMarkerMinCount) {
        //           xVal.forEach(() => {
        //             colors.push('transparent');
        //           });
        //         }
        //         colors[pn] = dataPoint.data.line.color;
        //         // point.data.marker = { color: colors };
        //         // eslint-disable-next-line no-undef
        //         if(!point.data.hovertemplateTemp) {
        //           point.data.hovertemplateTemp = point.data.hovertemplate;
        //         }
        //         // let hasTargetValue = false;
        //         // traceData[1].y.forEach(targetValue => {
        //         //   if(targetValue != null) {
        //         //     hasTargetValue = true;
        //         //   }
        //         // })
        //         // if(hasTargetValue) {
        //           // if(point.data.name == 'target') {
        //           //   targetNumber = point.pointNumber;
        //           // }
        //           // else {
        //           //   if(targetNumber == point.pointNumber) {
        //           //     if(point.data.hovertemplate == `<b>${this.lineChartGraphMapper[this.lineChartTitleMapper.autoEvalScore] + '<br />N/A'}</b><extra></extra>`) {
        //           //       point.data.hovertemplate = point.data.hovertemplateTemp;
        //           //     }
        //           //   }
        //           //   else {
        //           //     point.data.hovertemplate = `<b>${this.lineChartGraphMapper[this.lineChartTitleMapper.autoEvalScore] + '<br />N/A'}</b><extra></extra>`
        //           //   }
        //           // }
        //         // }
        //         pl.relayout(this.$refs.lineChart, point.data.marker, 0);
        //       }
        //     });
        //   } catch {
        //     // catch error
        //   }
        // });

        // mouse out on point
        // this.$refs.lineChart.on('plotly_unhover', (data) => {
        //   try {
        //     data.points.forEach((point) => {
        //       let dataPoint = point; //data.points[0];
        //       if (dataPoint && dataPoint.data && dataPoint.data.marker) {
        //         let pn = dataPoint.pointNumber;
        //         let colors = [];
        //         colors[pn] = dataPoint.data.line.color;
        //         if(showMarkerMinCount != -1 && xVal.length > showMarkerMinCount) {
        //           xVal.forEach(() => {
        //             colors.push('transparent');
        //           });
        //         }
        //         // point.data.marker = { color: colors };
        //         // eslint-disable-next-line no-undef
        //         Plotly.relayout(this.$refs.lineChart, point.data.marker, 0);
        //       }
        //     });
        //   } catch {
        //     // catch error
        //   }
        // });

        const viewBySelectComponent = this.$refs.viewBySelect;
        if (this.showLineChart && viewBySelectComponent) {
          document.querySelector('.wy-container-main').addEventListener('scroll', () => {
            try {
              if (viewBySelectComponent.blur && viewBySelectComponent.blur()) {
                viewBySelectComponent.blur();
              }
            } catch (error) {
              // do nothing
            }
          });
        }
      }
    },

    plotBarChart() {
      const waterfallChartPanelRefs = this.$refs.waterfallChartPanel;
      if (!waterfallChartPanelRefs) return;

      waterfallChartPanelRefs.style.display = 'block';

      // handled in gauge-summary.handlePanelChange logic
      if (!(this.networkData.autoEvalScore && this.networkData.autoEvalScore.barMetrics) || !this.barChartHasData) {
        this.barChartHasData = false;
        return;
      }

      const data = Object.entries(this.networkData.autoEvalScore.barMetrics).map(([label, value]) => ({ label: formatLabel(label), value: value }));
      // data.sort((a, b) => b.value - a.value);


      // const maxLength = 18; // Set your desired maximum length for the labels
      const xVal = data.map((item) => item.label);

      const yVal = data.map((item) => item.value);
      const xValCount = xVal.length;
      const maxNumOfSections = 5;

      // keep the bar width size smaller with multiple x null values
      for(let i=0; i < maxNumOfSections - xValCount; i++) {
        xVal.push(" ".repeat(i));
        yVal.push(null);
      }

      const charsNumber = 12;
      const xValLabel = [];
      for(let val of xVal) {
        const valIndexOf = val.indexOf(" ", charsNumber);
        if(valIndexOf > charsNumber) {
          const valReplaced =  Vue.WyUtils.replaceAt(val, valIndexOf, "||").split("||");

          const dVals = [];
          for(let dVal of valReplaced) {
            dVals.push(Vue.WyUtils.maxString(dVal, charsNumber, "..."));
          }
          xValLabel.push(dVals.join("<br>"));
        }
        else {
          xValLabel.push(Vue.WyUtils.maxString(val, charsNumber, "..."));
        }
      }
      
      const negativeOneCase = '0.00001'
      const barTrace = {
        x: xValLabel,
        // for y:, loop through each value, and if it's '-1', use 0
        y: yVal.map((item) => {
          if (item == negativeOneCase) return 0;
          return item;
        }),
        customdata: xVal,
        marker: {
          color: yVal.map((item, index) => {
            if (item == negativeOneCase) return 'black';
            return this.waterfallChartBarColors[index % this.waterfallChartBarColors.length];

          }),
        },
        text: yVal.map((item) => {
          // if (item == 1) return '100';
          if (item == negativeOneCase) return 'N/A';
          if (Number(item) == 0) return '0.00'
          if (Number(item) <= 0.1 && Number(item) > 0) return '< 0.1';
          return Number(item).toFixed(2);
        }),
        textposition: 'outside',
        type: 'bar',
        hoverinfo: 'none',
        hovertemplate: ' %{customdata}<extra></extra> ',
      };

      var traceData = [barTrace];

      var layout = {
        barmode: 'stack',
        paper_bgcolor: 'rgba(245,246,249,1)',
        plot_bgcolor: 'rgba(245,246,249,1)',
        showlegend: false,
        bargap: 0.6,
        // width: 0.15,
        margin: { pad: 8, t: 50 },
        hoverlabel: {
          bgcolor: '#303133',
          bordercolor: '#E6E6E6',
          font: {
            color: 'white'
          }
        },
        xaxis: {
          tickangle: 0,
          fixedrange: true,
          tickfont: {
            size: 14,
            family: 'Helvetica',
            weight: 500
          }
        },
        yaxis: {
          side: 'left',
          fixedrange: true,
          showticklabels: true,
          autorange: true,
          rangemode: 'tozero',
          tickfont: {
            size: 12,
            family: 'Helvetica',
          },
          title: {
            text: 'Average Score',
            font: {
              size: 12,
              color: '#000000',
              family: 'Helvetica',
            },
          },
        },
      };

      const waterfallChartRefs = this.$refs.waterfallChart;
      if (waterfallChartRefs) {
        // eslint-disable-next-line no-undef
        Plotly.newPlot(waterfallChartRefs, traceData, layout, { responsive: true, displayModeBar: false });
        this.calculateWaterfallHrLine();
      }
      // waterfallChartRefs.on('plotly_hover', (hoverData) => { 
      //   console.log(hoverData)  
      //   hoverData.event.preventDefault();
      //   hoverData.event.stopPropagation();
      //   try {
      //     if(this.$refs.hoverChart) {
      //       const gd = this.$refs.hoverChart;
      //       const pointX = hoverData.points[0].bbox.x0;
      //       const pointY = hoverData.points[0].bbox.y0 - 20;
      //       gd.style.left = pointX + 'px'
      //       gd.style.top = pointY + 'px'
      //       gd.style.display = "block";
      //       gd.innerHTML = 'uyuyuyyuyyuiiuuiy';
      //       console.log(gd)
      //     }
      //   }
      //   catch(error) {
      //     console.error(error)
      //   }
      // });
      // waterfallChartRefs.on('plotly_unhover', () => { 
      //   if(this.$refs.hoverChart) {
      //       const gd = this.$refs.hoverChart;
      //       gd.style.display = "none";
      //     }
      // })
    },
  },

  async created() {
    this.wyChart = new WyChartModule();
    window.addEventListener('resize', this.handleWindowResize);
  },

  destroyed() {
    window.removeEventListener('resize', this.handleWindowResize);
  },
});
</script>

<style lang="scss">
@media (max-width: 1320px) {
  .wy-auto-qa-panel-dynamic-chart {
    .dynamic-wrapper {
      // make into 2 rows
      grid-template-areas: 'item' 'item' !important;
      grid-template-columns: 100% !important;
    }
    .panel-wrapper {
      min-width: 700px;
    }
  }
}

.wy-auto-qa-panel-dynamic-chart {
  margin-bottom: 20px;
  min-height: 300px;

  .js-plotly-plot .plotly {
    .cursor-pointer {
      cursor: default !important;
    }
    .main-svg .xaxislayer-above g.xtick text {
      // font-size: 10px !important;
    }
  }

  .el-loading-mask {
    max-height: 100% !important;
  }

  .dynamic-wrapper {
    display: grid;
    grid-template-areas: 'item item';
    grid-template-columns: 1fr 1fr;
    grid-gap: 21px !important;
  }

  .panel-wrapper {
    height: 569px;
    border: 1px solid #cad2da;
    border-radius: 5px;
    background-color: white;
    overflow-x: auto;
    overflow-y: hidden;

    // hide scrollbar webkit
    &::-webkit-scrollbar {
      display: none;
    }
  }

  .panel-header {
    padding: 23px 16px 0px 25px;
    font-size: 22.8px;
    font-weight: 400;
    color: var(--site-font-chart-color);
    border-bottom: 0px solid #cad2da !important;
  }

  .panel-body-waterfall {
    // position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-content: center;
    height: 100%;
    margin-top: -45px;

    .plotly .main-svg {
      background-color: transparent !important;
      // position: relative;
      .xtick {
        text {
          font-size: 0.65vw !important;
        }
      }
      .hoverlayer{
        // display: none;
      }
    }
    .hover-chart {
      position: absolute;
      display: none;
    }
  }

  .panel-body {
    min-height: 300px;
    overflow: none;

    .info-section {
      display: flex;
      .date-range-info {
        padding-top: 0px;
        padding-left: 26px;
        padding-bottom: 0px;
        font-size: 14px;
        font-weight: 400;
        line-height: 2;
      }
    }
  }

  .panel-body-no-data {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;

    .content {
      max-width: 360px;
      font-weight: 500;
      line-height: 22px;
      font-size: 14px;
      text-align: center;
      text-align: center;
      color: #4b5158 !important;
    }
  }
  .panel-dropdown {
    display: flex;
    justify-content: flex-end;
    padding-top: 0px;
    padding-right: 40px;
    padding-bottom: 0px;
    margin-left: auto;
    min-width: unset !important;

    .el-select.date-range-dropdown.el-select--mini {
      width: 7.86vw !important;
      min-width: 140px;
      border: 1px #d9d9d9 solid;
      border-radius: 5px;
      position: relative;
      // right: 0.24vw;
      right: -0.76vw;
      top: -26px;
    }
    .el-select.date-range-dropdown.el-select--mini.blue-border {
      border: 1px #106fbc solid !important;
    }
    .el-select.date-range-dropdown.el-select--mini:active {
      border: 1px solid #1c4db2 !important;
    }

    input.el-input__inner {
      border-width: 0px 0px 1px 0px !important;
      border-radius: 5px;
      padding-left: 10px;
      font-size: 14px;
    }
    .el-input__inner {
      border: 1px solid transparent !important;
      &:hover {
        background: #f4f6f8;
      }
    }
  }

  .viewBy-dropdown {
    padding: 8px 12px;
    border-radius: 4px;
  }
  .viewBy-dropdown:hover {
    color: #409eff;
    border-color: #409eff;
  }

  .panel-footer {
    display: flex;
    align-items: center;
    justify-content: center !important;
    padding: 0px 20px 20px 20px;
    gap: 16px;
    font-weight: 400;
    border-top: 0px solid #cad2da !important;

    .footer-col {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 11.2px;
      font-weight: 400;
      padding-left: 30px;
    }
  }

  .waterfall-footer {
    border-top: none;
    background-color: red;
  }
  .dynamic-line-chart {
    .svg-container {
      // height: 400px !important;
    }
    .plotly .main-svg {
      background-color: transparent !important;
      .infolayer .g-ytitle .ytitle {
        font-family: Helvetica !important;
        font-size: 12.5px !important;
        fill: rgb(0, 0, 0) !important;
      }
    }
  }
}

.wy-auto-qa-date-range-popper {
  .el-select-dropdown__item.selected {
    color: #1a55ad !important;
    background-color: #e8f1ff !important;
    font-weight: 500;
  }
}


</style>
